import undefsafe from 'undefsafe'

import configSite from '../../config/sites'
import siteConfig from './siteConfig'
import { toTitle } from './formatString'
import { getCurrentSiteDomain } from './sites'
import {
  reportProductClick,
  reportProductAddToCartAttempt,
  reportModalPageView
} from '../analytics/resultAnalytics'
import { logError } from './errors'
import { getTopLevelRoute } from './urlHelper'

const getItemSmallPreviewUrl = (item) => {
  const previewPaths = undefsafe(configSite.defaults, 'ui.item_small_preview_paths')
  return previewPaths
    .map(path => undefsafe(item.previews, path))
    // return first non falsy value
    .reduce((prevPath, currPath) => {
      if (prevPath) {
        return prevPath
      }
      return currPath
    })
}

const formattedItem = (item, additionalProps = {}) => {
  let nextItem = Object.assign({}, item, additionalProps)
  // price
  // TODO: price_cents is missing from photodue search result...
  nextItem.price = item.price_cents ? item.price_cents.toString().slice(0, -2) : ''
  // classification
  nextItem.formattedClassification = item.classification && typeof item.classification === 'string'
    ? item.classification.split('/').map(category => toTitle(category)).join(' / ')
    : ''
  return nextItem
}

export const isSearchResultMatchesEqual = (mainMatches, comparingMatches) => {
  const hasSameLength = mainMatches.length === comparingMatches.length
  if (!hasSameLength) {
    return false
  }
  const comparisonResults = mainMatches.map((item, index) => {
    return item && comparingMatches && item.id === comparingMatches[index].id
  }).every(isIdEqual => isIdEqual)

  return comparisonResults
}

// https://github.com/envato/foundation-docs/blob/master/docs/shopfront_special_ui_rules.md
export const filterSortByOptions = (currentSite, parsedLocation, options) => {
  let filteredOptions = [...options]
  if (currentSite === 'photodune') {
    filteredOptions = filteredOptions.filter(options => {
      return options.value !== 'rating'
    })
  }

  // no `Best Match` if there's no search term
  if (!parsedLocation.term) {
    filteredOptions = filteredOptions.filter(options => {
      return options.value !== 'relevance'
    })
  }
  return filteredOptions
}

const getFooterLink = (item, currentSite) => {
  const footerLink = siteConfig(currentSite, 'item_view.footer_link')
  return footerLink && {
    label: footerLink.label,
    href: footerLink.href.replace(/\{(.*)\}/g, (match, field) => {
      return item[field]
    })
  }
}

export const itemDecorator = (currentSite, openModal, signedOut, favoriteCollectionID, collections, deleteBookmark, addBookmark, createFavoriteCollection, itemRankOffset, cartItems, location) => {
  return (item, index) => {
    const rank = index + itemRankOffset
    const topLevelRoute = getTopLevelRoute(location.pathname)
    const reportProductClickHandler = reportProductClick.bind(null, item, rank, currentSite, topLevelRoute)
    const reportProductAddToCartAttemptHandler = reportProductAddToCartAttempt.bind(null, item, rank, currentSite, topLevelRoute)
    const nonFavoriteCollections = Object.keys(collections)
      .map(collectionID => collections[collectionID])
      .filter(collection => collection.special_role !== 'favorite')
    const isItemInFavorites = collections[favoriteCollectionID] &&
      collections[favoriteCollectionID].bookmarks[item.id] &&
      collections[favoriteCollectionID].bookmarks[item.id].optimisticStatus !== 'deleted'
    const enhancedItem = formattedItem(item, {
      ui: {
        isInFavourites: isItemInFavorites,
        isInBookmarks: nonFavoriteCollections.some(collection => collection.bookmarks[item.id]),
        isInCart: cartItems.some(cartItemId => cartItemId.item_id === item.id),
        smallPreviewUrl: getItemSmallPreviewUrl(item),
        footerLink: getFooterLink(item, currentSite)
      }
    })
    const onAddToFavourites = (event) => {
      event.preventDefault()
      if (signedOut) {
        window.location = `//${getCurrentSiteDomain(currentSite)}/sign_in?action_name=favorite&resource_id=${item.id}&return_to=${window.location.pathname}${window.location.search}`
      } else {
        // check whether user has favorite collection
        if (favoriteCollectionID) {
          if (isItemInFavorites) {
            const bookmark = collections[favoriteCollectionID].bookmarks[item.id]
            if (bookmark.id && bookmark.optimisticStatus !== 'pending') {
              const isFavouriteCollection = true
              deleteBookmark(favoriteCollectionID, item.id, bookmark.id, isFavouriteCollection)
            }
          } else {
            const isFavouriteCollection = true
            addBookmark(favoriteCollectionID, item.id, isFavouriteCollection)
          }
        } else {
          createFavoriteCollection()
            .then((action) => {
              if (action && action.type === 'createCollectionSuccess') {
                const newCollectionId = action.payload.id
                // this is used for ga tracking so we know it's add to favourite collection
                const isFavouriteCollection = true
                return addBookmark(
                  newCollectionId,
                  item.id,
                  isFavouriteCollection
                )
              }
            }).catch(logError)
        }
      }
    }
    const onAddToCollections = (event) => {
      event.preventDefault()
      if (signedOut) {
        window.location = `//${getCurrentSiteDomain(currentSite)}/sign_in?action_name=collection&resource_id=${item.id}&return_to=${window.location.pathname}${window.location.search}`
      } else {
        reportModalPageView({
          title: 'Modal: Add item to collection',
          page: `${window.location.pathname}/add-item-to-collection${window.location.search}`
        })
        openModal({
          type: 'addItemToCollection',
          payload: {
            item
          }
        })
      }
    }
    const onAddToCart = (event) => {
      event.preventDefault()
      reportProductAddToCartAttemptHandler()

      reportModalPageView({
        title: 'Modal: Customize item before adding to cart',
        page: `${window.location.pathname}/customize-item-before-adding-to-cart${window.location.search}`
      })
      openModal({
        type: 'addItemToCart',
        payload: {
          item: enhancedItem
        }
      })
    }

    return {
      item: enhancedItem,
      rank,
      onItemLinkClick: reportProductClickHandler,
      onAddToCart,
      onAddToFavourites,
      onAddToCollections
    }
  }
}



// WEBPACK FOOTER //
// ./common/utils/item.js